import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const fmt = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

export default function EMICalculator({ embedded = false }) {
  const [amount, setAmount] = useState(1500000);
  const [rate, setRate] = useState(10.5);
  const [tenure, setTenure] = useState(48);

  const { emi, totalInterest, total } = useMemo(() => {
    const r = rate / 12 / 100;
    const n = tenure;
    const emi = r === 0 ? amount / n : (amount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const total = emi * n;
    return { emi, totalInterest: total - amount, total };
  }, [amount, rate, tenure]);

  const data = [
    { name: "Principal", value: amount, color: "hsl(158 64% 28%)" },
    { name: "Interest", value: totalInterest, color: "hsl(222 47% 22%)" },
  ];

  return (
    <div data-testid="emi-calculator" className={`grid md:grid-cols-5 gap-8 ${embedded ? '' : 'py-4'}`}>
      <div className="md:col-span-3 space-y-8">
        <SliderRow label="Loan Amount" value={amount} onChange={setAmount} min={100000} max={20000000} step={50000} format={fmt} testid="emi-amount" />
        <SliderRow label="Interest Rate (p.a.)" value={rate} onChange={setRate} min={7} max={22} step={0.1} format={(v) => v.toFixed(1) + " %"} testid="emi-rate" />
        <SliderRow label="Tenure (months)" value={tenure} onChange={setTenure} min={12} max={84} step={1} format={(v) => v + " mo"} testid="emi-tenure" />

        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-200">
          <Stat label="Monthly EMI" value={fmt(emi)} big testid="emi-monthly" />
          <Stat label="Interest" value={fmt(totalInterest)} testid="emi-interest" />
          <Stat label="Total Payable" value={fmt(total)} testid="emi-total" />
        </div>
      </div>
      <div className="md:col-span-2">
        <div className="aspect-square relative min-h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} innerRadius="70%" outerRadius="95%" paddingAngle={2} dataKey="value" stroke="none">
                {data.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip formatter={(v) => fmt(v)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="text-center">
              <p className="text-xs uppercase tracking-widest text-slate-500">EMI</p>
              <p className="font-display text-3xl md:text-4xl text-slate-900">{fmt(emi)}</p>
              <p className="text-xs text-slate-500 mt-1">per month</p>
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <Legend color="hsl(158 64% 28%)" label="Principal" />
          <Legend color="hsl(222 47% 22%)" label="Interest" />
        </div>
      </div>
    </div>
  );
}

function SliderRow({ label, value, onChange, min, max, step, format, testid }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="text-sm uppercase tracking-widest text-slate-500">{label}</label>
        <p data-testid={`${testid}-value`} className="font-display text-2xl text-emerald-800">{format(value)}</p>
      </div>
      <div className="mt-3">
        <Slider data-testid={testid} value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} />
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-slate-400">
        <span>{format(min)}</span><span>{format(max)}</span>
      </div>
    </div>
  );
}

function Stat({ label, value, big, testid }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
      <p data-testid={testid} className={`font-display ${big ? 'text-3xl md:text-4xl text-emerald-800' : 'text-xl md:text-2xl text-slate-900'}`}>{value}</p>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-2 text-slate-600">
      <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </div>
  );
}
